import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import lambdaFetch from '../functions/FetchFromLambda';

const { REACT_APP_STAGE } = process.env;

// Get all available timezones using Intl API
const getAllTimezones = () => {
  try {
    if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (error) {
    console.error('Error getting timezones from Intl API:', error);
  }
  
  // Fallback to common timezones if Intl.supportedValuesOf is not available
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Phoenix',
    'America/Toronto',
    'America/Vancouver',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'UTC',
  ];
};

export default function SystemSettings(props) {
  const { credentials } = props.fetchInitialData;
  const [timezone, setTimezone] = useState('America/New_York'); // Default to ET
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [timezoneItemExists, setTimezoneItemExists] = useState(false);
  const [currentObjId, setCurrentObjId] = useState(null);

  // Get all available timezones
  const allTimezones = useMemo(() => getAllTimezones(), []);

  // Fetch current timezone setting
  const fetchTimezone = async () => {
    setLoading(true);
    setError(null);

    try {
      // Scan the client-values table for timezone setting
      const endpoint = 'dynamo/client-values/scan';
      const requestBody = {
        limit: 1000,
        filterExpression: '#key = :key',
        expressionAttributeNames: {
          '#key': 'R_KEY',
        },
        expressionAttributeValues: {
          ':key': 'timezone',
        },
      };

      const response = await lambdaFetch(
        endpoint,
        'post',
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        '',
        credentials
      );

      console.log('Timezone fetch response:', response);

      if (response && response.data) {
        let items = [];
        
        if (Array.isArray(response.data)) {
          items = response.data;
        } else if (response.data.body) {
          let bodyObj = response.data.body;
          if (typeof response.data.body === 'string') {
            bodyObj = JSON.parse(response.data.body);
          }
          let finalData = bodyObj?.data;
          if (typeof bodyObj?.data === 'string') {
            finalData = JSON.parse(bodyObj.data);
          }
          items = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
        } else if (response.data.data) {
          items = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        }

        console.log('Parsed items:', items);

        // Find timezone setting
        const timezoneItem = items.find(item => item.R_KEY === 'timezone');
        
        if (timezoneItem) {
          console.log('Found timezone item:', timezoneItem);
          setTimezone(timezoneItem.value || timezoneItem.R_VALUE || 'America/New_York');
          setCurrentObjId(timezoneItem.OBJ_ID);
          setTimezoneItemExists(true);
        } else {
          console.log('No timezone setting found, using default');
          setTimezone('America/New_York');
          setTimezoneItemExists(false);
          setCurrentObjId(null);
        }
      }
    } catch (err) {
      console.error('Error fetching timezone:', err);
      setError(`Failed to load timezone setting: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save timezone setting
  const handleSave = async () => {
    setSaving(true);

    try {
      let response;

      if (timezoneItemExists && currentObjId) {
        // Update existing timezone setting
        const endpoint = `dynamo/client-values/item/${currentObjId}/timezone`;
        const updates = {
          value: timezone,
          R_VALUE: timezone,
          description: 'System timezone setting',
          R_DESCRIPTION: 'System timezone setting',
          active: true,
          R_ACTIVE: 1,
        };

        response = await lambdaFetch(
          endpoint,
          'put',
          credentials.user.accessToken,
          JSON.stringify(updates),
          'Timezone updated successfully',
          credentials
        );
      } else {
        // Create new timezone setting
        const endpoint = 'dynamo/client-values';
        const newItem = {
          OBJ_ID: `TIMEZONE_${Date.now()}`,
          R_KEY: 'timezone',
          value: timezone,
          R_VALUE: timezone,
          description: 'System timezone setting',
          R_DESCRIPTION: 'System timezone setting',
          active: true,
          R_ACTIVE: 1,
          DELETE_FLAG: 0,
        };

        response = await lambdaFetch(
          endpoint,
          'post',
          credentials.user.accessToken,
          JSON.stringify(newItem),
          'Timezone created successfully',
          credentials
        );

        if (response && response.success) {
          setTimezoneItemExists(true);
          setCurrentObjId(newItem.OBJ_ID);
        }
      }

      if (response && response.success) {
        setSnackbarMessage('Timezone setting saved successfully! Page will reload in 2 seconds...');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Refresh to confirm the save
        await fetchTimezone();
        
        // Save current tab to sessionStorage so we can return to it after reload
        sessionStorage.setItem('adminTabAfterReload', 'system-settings');
        
        // Reload the page after 2 seconds to apply new timezone throughout the app
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        throw new Error('Failed to save timezone setting');
      }
    } catch (err) {
      console.error('Error saving timezone:', err);
      setSnackbarMessage(`Failed to save timezone: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  // Load timezone on mount
  useEffect(() => {
    fetchTimezone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          System Settings
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Timezone Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set the system timezone for your application. This affects how dates and times 
                are displayed throughout the application. The timezone setting applies to all users.
              </Typography>

              <Autocomplete
                fullWidth
                value={timezone}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setTimezone(newValue);
                  }
                }}
                options={allTimezones}
                disabled={saving}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="System Timezone"
                    placeholder="Type to search (e.g., New_York, London, Tokyo)..."
                    helperText={`${allTimezones.length} timezones available - Current: ${timezone}`}
                  />
                )}
                sx={{ mb: 3 }}
                ListboxProps={{
                  style: { maxHeight: 400 },
                }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchTimezone}
                disabled={loading || saving}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? 'Saving...' : 'Save Timezone'}
              </Button>
            </Box>

            {timezoneItemExists && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                  This timezone setting is stored in DynamoDB table: apix_{REACT_APP_STAGE}_sys_client_values
                  <br />
                  Key: timezone | Value: {timezone}
                </Alert>
              </Box>
            )}
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ fontSize: '0.875rem' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
